import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FormikValues } from 'formik';
import { UilClock, UilUser } from '@iconscout/react-unicons';
import {
  useUpdateLocationBlackoutMutation,
  useUpdatePhysicianBlackoutMutation,
} from '@api/blackout/blackoutAPI';
import { useModal } from '@components/Modal/context/useModalContext';
import { ActionType } from '@components/Table/utils/actionsFormatter';
import { convertTimesBlackout } from '@helpers/convertTimes';
import { errorAlert, successAlert } from '@helpers/alerts/alerts';
import { IDaySlotProps } from '@pages/Schedule/components/Day/types';
import { Slot } from '@pages/Schedule/components/Slot';
import { minuteHeight } from '@pages/Schedule/constants/ScheduleSettings';
import { useLanguageContext } from '@hooks/useLanguageContext';
import { blackoutFormModal } from '@pages/Schedule/components/Blackout/BlackoutModal';
import { BlackoutType } from '@pages/Schedule/components/Blackout/type';
import { useScheduleContext } from '@pages/Schedule/context/useScheduleContext';
import { processBlackoutResult } from '@pages/Schedule/utils/blackoutUtils';
import { Routes } from '../../../../router';

export const DaySlot = ({
  patient,
  isAdult,
  duration,
  appointmentId,
  physicianId,
  locationId,
  patientNumber,
  eventType,
  personalBlackout,
}: IDaySlotProps) => {
  const slotHeight = `${minuteHeight * duration}px`;

  const { refetch } = useScheduleContext();

  const { dictionary } = useLanguageContext();

  const { openModal } = useModal();

  const hours = Math.floor(duration / 60);

  const formattedHours = hours > 0 ? `${hours}h` : '';

  const minutes = duration % 60;

  const formattedMinutes = minutes > 0 ? `${minutes}m` : '';

  const slotDetails = (
    <div className="slot-details">
      <div className="slot-details-row slot-details-title">
        <span className="slot-details-icon">
          <UilUser size={16} />
        </span>
        <span className="slot-details-label">{`${patient} ${patientNumber ? `(${patientNumber})` : ''}`}</span>
      </div>
      <div className="slot-details-row slot-details-info">
        <span className="slot-details-icon">
          <UilClock size={16} />
        </span>
        <span className="slot-details-label">{`${formattedHours} ${formattedMinutes} - ${eventType || ''}`}</span>
      </div>
    </div>
  );

  const nameType =
    eventType === 'Blackout'
      ? 'Sub-I coverage for PI absence '
      : 'Location closed - On-Call Coverage Only';

  const slotDetailsBlackout = (
    <div className="slot-details">
      <div className="slot-details-row slot-details-info">
        <span className="slot-details-icon">
          <UilClock size={16} />
        </span>
        <span className="slot-details-label">
          {`${formattedHours} ${formattedMinutes} - ${eventType !== 'Unavailable' ? nameType : eventType}`}
        </span>
      </div>
    </div>
  );

  const navigate = useNavigate();

  const onClick = () => {
    navigate(`${Routes.Appointments}/${appointmentId}`);
  };

  const [updateLocationBlackout] = useUpdateLocationBlackoutMutation();

  const [updatePhysicianBlackout] = useUpdatePhysicianBlackoutMutation();

  const schedule = useScheduleContext();

  const { setBlackoutErrorMessage } = schedule;

  const handleBlackoutSubmit = async (values: FormikValues, closeModal: () => void) => {
    try {
      const valuesBlackoutForm = convertTimesBlackout(values);

      const blackoutFunction = valuesBlackoutForm?.locationId
        ? updateLocationBlackout
        : updatePhysicianBlackout;

      const result = await processBlackoutResult(valuesBlackoutForm, blackoutFunction, closeModal);

      if (result.success === 1) {
        successAlert('Blackout updated successfully');
        refetch();
      }

      if (result.success === 2 && result.data) {
        const appointmentSummary = result.data.conflictedAppointments
          .map((appointment) => `${appointment.name}: ${appointment.time}`)
          .join(', ');

        setBlackoutErrorMessage(appointmentSummary);
      }

      if (result.success === 0) {
        errorAlert(result.message);
      }
    } catch (error) {
      errorAlert(
        (
          error as {
            data?: { message?: string };
          }
        )?.data?.message || 'An unexpected error occurred while processing the blackout.',
      );
    }
  };

  const {
    id = '',
    from = '',
    to = '',
    from_time: fromTime = '',
    to_time: toTime = '',
  } = personalBlackout ?? {};

  const initialValuesBlackout = {
    id,
    physicianId,
    locationId,
    from,
    to,
    from_time: fromTime,
    to_time: toTime,
  };

  const initialValuesBlackoutLocation = {
    id,
    locationId,
    from,
    to,
    from_time: fromTime,
    to_time: toTime,
  };

  const onClickBlackout = () =>
    blackoutFormModal(
      openModal,
      handleBlackoutSubmit,
      dictionary,
      BlackoutType.physician,
      ActionType.edit,
      initialValuesBlackout,
      id,
    );

  const onClickBlackoutLocation = () =>
    blackoutFormModal(
      openModal,
      handleBlackoutSubmit,
      dictionary,
      BlackoutType.location,
      ActionType.edit,
      initialValuesBlackoutLocation,
      id,
    );

  return (
    <div
      className="schedule-cell-content"
      style={{
        ['height' as string]: slotHeight,
      }}
    >
      {!patient && eventType === 'Unavailable' && (
        <Slot details={<span className="text-semibold">{dictionary.labels.unavailable}</span>} />
      )}
      {patient && (
        <Slot
          variant={isAdult ? 'success' : 'warning'}
          details={slotDetails}
          handleClick={onClick}
          eventName="appointment"
        />
      )}
      {eventType === 'Blackout' && (
        <Slot
          variant="blackout"
          details={slotDetailsBlackout}
          handleClick={() => onClickBlackout()}
        />
      )}
      {eventType === 'Blackout Location' && (
        <Slot
          variant="blackout-location"
          details={slotDetailsBlackout}
          handleClick={() => onClickBlackoutLocation()}
        />
      )}
    </div>
  );
};
